import { useInterval } from 'ahooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { API } from '@/api'
import { ServerDomainInfo } from '@/api/type-server.ts'
import {
  Button,
  ButtonRadio,
  Divider,
  Loading,
  message,
  Modal,
  Pagination,
  Trans,
} from '@/components'
import { Dropdown } from '@/components/base/Dropdown.tsx'
import { CollapseTableRow } from '@/components/CollapseTableRow'
import { EmptyView } from '@/components/EmptyView.tsx'
import { Instance } from '@/models/instance'
import { formatDomain, formatPrice } from '@/utils/formatter'
import { TIME_UNITS_IN_MILLS } from '@/utils/time'

import { CreateSnapshotModal } from './components/CreateSnapshotModal'
import { EditableInstanceName } from './components/EditableInstanceName.tsx'
import { InstanceDetail } from './components/InstanceDetail'

interface FilterItem {
  label: ReactNode
  value: string
  codes: number[]
}

const showSnapshotBtnMap: Record<number, boolean> = {
  30: false, // image id
  27: false,
}

const REGION_GLOBAL = -1
const REGION_DATA_DRIVE = -2
const REGION_MESH_CLUSTERING = -3

export function InstanceListPage() {
  const statusFilters: FilterItem[] = [
    {
      label: <Trans i18nKey="instance.list.statusLabelRunning">Running</Trans>,
      value: 'active',
      codes: [1, 2, 7, 15, 16],
    },
    {
      label: <Trans i18nKey="instance.list.statusLabelSnapshotting">Snapshotting</Trans>,
      value: 'snapshotting',
      codes: [8, 9, 10, 11, 12],
    },
    {
      label: <Trans i18nKey="instance.list.statusLabelReleased">Released</Trans>,
      value: 'released',
      codes: [3, 4, 6],
    },
  ]

  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [instaceList, setInstanceList] = useState<Instance[]>([])
  const [filter, setFilter] = useState<FilterItem | undefined>(statusFilters[0])
  const [createSnapshotInstance, setCreateSnapshotInstance] = useState<Instance | undefined>()
  const [loading, setLoading] = useState(false)
  const pageSize = 10
  const [domainInfos, setDomainInfos] = useState<ServerDomainInfo[]>([])
  const [regions, setRegions] = useState<number[]>([])
  const [regionFilterCriteria, setRegionFilterCriteria] = useState<number>(REGION_GLOBAL)
  const { t } = useTranslation()

  const getDomains = useCallback(async () => {
    try {
      const res = await API.getDomainList()
      if (res.code === 0) {
        setDomainInfos(res.domainInfos)
        setRegions(res.domainInfos.map((info) => info.domain))
      }
    } catch (error) {
      console.error(error)
    }
  }, [])

  useEffect(() => {
    getDomains()
  }, [])

  const getNodeList = useCallback(
    async (p = page, force: boolean) => {
      if (force) {
        setLoading(true)
      }
      try {
        let apiRegionFilterCriteria = 0
        if (regionFilterCriteria === REGION_GLOBAL) {
          apiRegionFilterCriteria = 0
        } else if (regionFilterCriteria === REGION_DATA_DRIVE) {
          apiRegionFilterCriteria = 1
        } else if (regionFilterCriteria === REGION_MESH_CLUSTERING) {
          apiRegionFilterCriteria = 2
        }

        const res = await API.getNodeList({
          page: p,
          per_page: pageSize,
          statuses: filter?.codes.join(','),
          regions: regions,
          region_filter_criteria: apiRegionFilterCriteria,
        })

        if (res.code === 0) {
          setPage(res.pagination.page)
          setTotal(res.pagination.total)
          setInstanceList(res.userNodes.map((data) => new Instance(data)))
        }
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    },
    [page, filter, regions, regionFilterCriteria],
  )

  useEffect(() => {
    getNodeList(1, true)
  }, [filter, regions, regionFilterCriteria])

  const onRelease = useCallback(async (data: Instance) => {
    await Modal.confirm({
      title: t('instance.releaseModal.title'),
      content: (
        <div className="bg-panel-gray-100 rounded-lg p-4 flex-row mb-4">
          <div>
            <div className="text-[20px] leading-7 font-bold  text-primary">{data.displayTitle}</div>
            {/* <div className="t-sb font-medium text-secondary">{instance.displayTitle}</div> */}
          </div>
          {/* <Divider className="my-3" />
          <div>
            <div className="t-sb text-red-500 font-medium">Environment was not saved</div>
          </div> */}
          <Divider className="my-3" />
          <div>
            <div className="t-sb text-secondary font-medium">
              <Trans i18nKey="instance.releaseModal.content">
                The machine will be stopped and released, please save the environment if you need
                it.
              </Trans>
            </div>
            <div className=" text-btn-secondary t-sb cursor-pointer">
              <Trans i18nKey="instance.releaseModal.whySaveEnvironment">
                Why save the environment?
              </Trans>
            </div>
          </div>
        </div>
      ),
      confirmBtnCfg: {
        text: t('instance.releaseModal.stopReleaseBtn'),
      },
      className: 'w-[800px]',
    })
    const res = await API.deleteInstance({ id: data.id })
    if (res.code === 0) {
      message.success(t('common.message.success'))
      getNodeList(page, true)
    }
  }, [])

  // adjust interval by instance status
  const refreshInterval = useMemo(() => {
    if (Array.isArray(instaceList) && instaceList.length > 0) {
      const contains = instaceList.findIndex((item) => item.inProgress)
      if (contains >= 0) {
        return TIME_UNITS_IN_MILLS.SECOND
      }
    }
    return 5 * TIME_UNITS_IN_MILLS.SECOND
  }, [instaceList])

  useInterval(
    () => {
      getNodeList(page, false)
    },
    refreshInterval,
    { immediate: true },
  )

  const onUpdate = () => {
    getNodeList(page, true)
  }

  return (
    <div className="card bg-white h-full overflow-y-auto">
      <div className="flex h-[60px] items-start justify-between">
        <div className="h6">
          <Trans i18nKey="instance.title">My Deployments</Trans>
        </div>
        <Link to="/create">
          <Button type="secondary">
            <Trans i18nKey="instance.createBtn">Deploy</Trans>
          </Button>
        </Link>
      </div>

      <Divider />

      <div className="flex gap-8 mb-4">
        <div className="flex flex-col gap-2">
          <div className="text-secondary t-sb">
            <Trans i18nKey="instance.list.statusLabel">Instance Status</Trans>
          </div>
          <div>
            <ButtonRadio
              value={filter?.value}
              items={statusFilters}
              onChange={(e) => {
                setFilter(statusFilters.find((i) => i.value === e))
              }}
            />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="text-secondary t-sb">
            <Trans i18nKey="create.regionLabel">Region</Trans>
          </div>
          <div>
            <Dropdown
              rootClassName="t-sb w-60"
              className="h-11"
              options={[
                { label: t('common.region.groupGlobal'), value: REGION_GLOBAL, className: 't-sm' },
                {
                  label: t('common.region.groupDataDrive'),
                  value: REGION_DATA_DRIVE,
                  className: 't-sm',
                },
                ...domainInfos.map((info, index) => ({
                  label: t(`common.region.domain${info.domain}`),
                  value: info.domain,
                  className: 't-sm',
                  separatorBefore: index === 0,
                })),
              ]}
              value={regionFilterCriteria}
              onChange={(value) => {
                const numValue = Number(value)
                setRegionFilterCriteria(numValue)

                if (numValue === REGION_GLOBAL) {
                  setRegions(domainInfos.map((info) => info.domain))
                } else if (numValue === REGION_DATA_DRIVE || numValue === REGION_MESH_CLUSTERING) {
                  setRegions([])
                } else {
                  setRegions([numValue])
                }
              }}
            />
          </div>
        </div>
      </div>

      {loading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}
      {!loading && instaceList.length === 0 && (
        <div className="mt-4">
          <EmptyView
            title={
              <span className="t-sm text-secondary">
                <Trans i18nKey="common.emptyViewNoDataMsg" />
              </span>
            }
          />
        </div>
      )}
      {!loading && instaceList.length > 0 && (
        <table className="table-card mt-4 table-fixed">
          <thead>
            <tr>
              <th colSpan={1}>
                <Trans i18nKey="instance.tableHeaderID">ID</Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="instance.tableHeaderName">Name</Trans>
              </th>
              <th colSpan={1}>
                <Trans i18nKey="instance.tableHeaderCost">Cost</Trans>
              </th>
              <th colSpan={3}>
                <Trans i18nKey="instance.tableHeaderAction">Action</Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {instaceList.map((data) => (
              <CollapseTableRow
                key={data.id}
                colSpan={7}
                tableClassName={'!border-none ring-1 ring-[#E1E4EA] rounded-xl group'}
                content={
                  <InstanceDetail
                    instance={data}
                    onUpdate={onUpdate}
                    isRunning={filter?.label === 'Running'}
                  />
                }
              >
                <td colSpan={1} className="t-sm">
                  {data.resID}
                </td>
                <td colSpan={2}>
                  <div className="flex items-center group">
                    <EditableInstanceName instance={data} onUpdate={onUpdate} />
                  </div>
                  <div className="t-xs mt-0.5 flex gap-2">
                    <span className="text-tertiary">Region</span>
                    <span className="text-secondary">
                      {domainInfos.find((info) => info.domain === data.domain)?.displayName ||
                        formatDomain(data.domain)}
                    </span>
                    <span className="text-secondary">·</span>
                    <span className={data.statusTextStyle}>{data.statusText}</span>
                  </div>
                </td>
                <td colSpan={1}>{formatPrice(data.cost, false)}</td>
                <td colSpan={2}>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-4">
                      {data.canRelease && (
                        <div
                          className="text-btn-secondary cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation()
                            onRelease(data)
                          }}
                        >
                          <Trans i18nKey="instance.list.action.release">Release</Trans>
                        </div>
                      )}
                      {data.canSnapshot && (showSnapshotBtnMap[data.node.image.id] ?? true) && (
                        <div
                          className="text-btn-secondary cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation()
                            setCreateSnapshotInstance(data)
                          }}
                        >
                          <Trans i18nKey="instance.list.action.takeSnapshot">Take Snapshot</Trans>
                        </div>
                      )}

                      {!data.canRelease && !data.canSnapshot && '-'}
                    </div>
                  </div>
                </td>
              </CollapseTableRow>
            ))}
          </tbody>
        </table>
      )}

      {total > 0 && !loading && (
        <Pagination
          size={pageSize}
          total={total}
          current={page}
          className="justify-end mt-4"
          onChange={(page) => getNodeList(page, true)}
        />
      )}

      {createSnapshotInstance ? (
        <CreateSnapshotModal
          onClose={() => setCreateSnapshotInstance(undefined)}
          instance={createSnapshotInstance}
        />
      ) : null}
    </div>
  )
}
